import { Injectable, inject, computed } from '@angular/core';
import { Store } from '@ngrx/store';
import { Option } from '@sapphire/result';
import {
  selectLoading,
  selectSlowLoading,
  selectError,
  selectSurveys,
  selectFilters,
  selectSort,
  selectPagination,
  selectPage,
  selectHasMore,
  selectContext,
  selectCounts,
  selectChildSurveys,
  selectChildSurveyErrors,
  selectActiveTabId,
} from './survey-list/survey-list.selectors';
import {
  selectBuyerProjectManagers,
  selectBuyerSettings,
  selectIsDownloadingCsReport,
  selectIsDownloadingSupplierSummary,
  selectIsDownloadingSurveyReport,
  selectOperatorProjectManagers,
  selectTransactionStatuses,
} from './reference/survey-reference.selectors';
import {
  selectSurvey,
  selectManageSurveyLoading,
  selectManageSurveyError,
  selectChildSurvey,
  selectChildSurveyLoading,
  selectChildSurveyError,
  selectHealthMetrics,
  selectHealthMetricsLoading,
  selectHealthMetricsError,
  selectRemainingHours,
  selectRemainingHoursLoading,
  selectRemainingHoursError,
  selectActivityLogs,
  selectActivityLogsLoading,
  selectActivityLogsError,
  selectQuotas,
  selectQuotaTotal,
  selectQuotasLoadingMore,
  selectQuotaLockingIds,
  selectQuotaSavingIds,
  selectTrafficChannels,
  selectTrafficChannelsLoading,
  selectTrafficChannelsError,
  selectTcStatusChangingIds,
  selectCreatingTrafficChannel,
  selectTrafficChannelCreationError,
  selectPsidUploadLoading,
  selectPsidUploadError,
  selectPsidUploadFilePath,
  selectPsidUploadUniqCount,
  selectPsidUploadTotalCount,
} from './manage-survey/manage-survey.selectors';

@Injectable({ providedIn: 'root' })
export class SurveySelectorsFacade {
  private readonly _store = inject(Store);

  private readonly _error = this._store.selectSignal(selectError);
  private readonly _context = this._store.selectSignal(selectContext);

  public readonly loading = this._store.selectSignal(selectLoading);
  public readonly slowLoading = this._store.selectSignal(selectSlowLoading);

  public readonly error = computed(() => Option.from(this._error()));

  public readonly surveys = this._store.selectSignal(selectSurveys);
  public readonly counts = this._store.selectSignal(selectCounts);

  public readonly filters = this._store.selectSignal(selectFilters);

  public readonly sort = this._store.selectSignal(selectSort);

  public readonly pagination = this._store.selectSignal(selectPagination);
  public readonly page = this._store.selectSignal(selectPage);
  public readonly hasMore = this._store.selectSignal(selectHasMore);

  public readonly activeTabId = this._store.selectSignal(selectActiveTabId);

  public readonly context = computed(() => Option.from(this._context()));

  public readonly childSurveysMap =
    this._store.selectSignal(selectChildSurveys);

  public readonly childSurveyErrorsMap = this._store.selectSignal(
    selectChildSurveyErrors,
  );

  // Reference data
  public readonly buyerProjectManagers = this._store.selectSignal(
    selectBuyerProjectManagers,
  );

  public readonly operatorProjectManagers = this._store.selectSignal(
    selectOperatorProjectManagers,
  );

  public readonly transactionStatuses = this._store.selectSignal(
    selectTransactionStatuses,
  );

  public readonly buyerSettings = this._store.selectSignal(selectBuyerSettings);

  public readonly isDownloadingSurveyReport = this._store.selectSignal(
    selectIsDownloadingSurveyReport,
  );

  public readonly isDownloadingSupplierSummary = this._store.selectSignal(
    selectIsDownloadingSupplierSummary,
  );

  public readonly isDownloadingCsReport = this._store.selectSignal(
    selectIsDownloadingCsReport,
  );

  // Survey Preview selectors
  public readonly survey = this._store.selectSignal(selectSurvey);

  public readonly childSurvey = this._store.selectSignal(selectChildSurvey);

  public readonly healthMetrics = this._store.selectSignal(selectHealthMetrics);

  public readonly healthMetricsLoading = this._store.selectSignal(
    selectHealthMetricsLoading,
  );

  public readonly healthMetricsError = this._store.selectSignal(
    selectHealthMetricsError,
  );

  public readonly remainingHours =
    this._store.selectSignal(selectRemainingHours);

  public readonly remainingHoursLoading = this._store.selectSignal(
    selectRemainingHoursLoading,
  );

  public readonly remainingHoursError = this._store.selectSignal(
    selectRemainingHoursError,
  );

  public readonly activityLogs = this._store.selectSignal(selectActivityLogs);

  public readonly activityLogsLoading = this._store.selectSignal(
    selectActivityLogsLoading,
  );

  public readonly activityLogsError = this._store.selectSignal(
    selectActivityLogsError,
  );

  public readonly childSurveyLoading = this._store.selectSignal(
    selectChildSurveyLoading,
  );

  public readonly childSurveyError = this._store.selectSignal(
    selectChildSurveyError,
  );

  public readonly manageSurveyLoading = this._store.selectSignal(
    selectManageSurveyLoading,
  );

  public readonly manageSurveyError = this._store.selectSignal(
    selectManageSurveyError,
  );

  public readonly quotas = this._store.selectSignal(selectQuotas);

  public readonly quotaTotal = this._store.selectSignal(selectQuotaTotal);

  public readonly quotasLoadingMore = this._store.selectSignal(
    selectQuotasLoadingMore,
  );

  public readonly quotaLockingIds = this._store.selectSignal(
    selectQuotaLockingIds,
  );

  public readonly quotaSavingIds =
    this._store.selectSignal(selectQuotaSavingIds);

  public readonly trafficChannels = this._store.selectSignal(
    selectTrafficChannels,
  );

  public readonly trafficChannelsLoading = this._store.selectSignal(
    selectTrafficChannelsLoading,
  );

  public readonly trafficChannelsError = this._store.selectSignal(
    selectTrafficChannelsError,
  );

  public readonly tcStatusChangingIds = this._store.selectSignal(
    selectTcStatusChangingIds,
  );

  public readonly creatingTrafficChannel = this._store.selectSignal(
    selectCreatingTrafficChannel,
  );

  public readonly trafficChannelCreationError = this._store.selectSignal(
    selectTrafficChannelCreationError,
  );

  public readonly psidUploadLoading = this._store.selectSignal(
    selectPsidUploadLoading,
  );

  public readonly psidUploadError = this._store.selectSignal(
    selectPsidUploadError,
  );

  public readonly psidUploadFilePath = this._store.selectSignal(
    selectPsidUploadFilePath,
  );

  public readonly psidUploadUniqCount = this._store.selectSignal(
    selectPsidUploadUniqCount,
  );

  public readonly psidUploadTotalCount = this._store.selectSignal(
    selectPsidUploadTotalCount,
  );
}
